import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import logo from '../../assets/logo.png';
import { clearAuth, getStoredUser, linkTelegramId } from '../../api/auth';
import { getTariffs, createPayment, getSubscription } from '../../api/vpn';
import './Dashboard.css';

function Dashboard() {
  const navigate = useNavigate();
  const user = getStoredUser();
  const [activeTab, setActiveTab] = useState('subscription');
  const [tariffs, setTariffs] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subLoading, setSubLoading] = useState(true);
  const [selectedTariff, setSelectedTariff] = useState(null);
  const [promoCode, setPromoCode] = useState('');
  const [processing, setProcessing] = useState(false);
  const [telegramId, setTelegramId] = useState('');
  const [linkingTelegram, setLinkingTelegram] = useState(false);

  useEffect(() => {
    loadTariffs();
    loadSubscription();
    // Загружаем telegram_id из user
    if (user?.telegram_id) {
      setTelegramId(user.telegram_id);
    }
  }, []);

  const loadTariffs = async () => {
    try {
      const data = await getTariffs();
      setTariffs(data);
    } catch (error) {
      console.error('Failed to load tariffs:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSubscription = async () => {
    try {
      const data = await getSubscription();
      setSubscription(data);
    } catch (error) {
      console.error('Failed to load subscription:', error);
    } finally {
      setSubLoading(false);
    }
  };

  const handlePayment = async (tariffKey) => {
    setProcessing(true);
    setSelectedTariff(tariffKey);
    
    try {
      const payment = await createPayment(tariffKey, promoCode || null);
      window.location.href = payment.confirmationUrl;
    } catch (error) {
      console.error('Payment error:', error);
      
      let errorMessage = 'Ошибка создания платежа. Попробуйте снова.';
      if (error.message) {
        errorMessage = error.message;
      }
      
      alert(errorMessage);
      setProcessing(false);
      setSelectedTariff(null);
    }
  };

  const handleLinkTelegram = async () => {
    console.log('Current telegramId state:', telegramId);
    
    if (!telegramId.trim()) {
      alert('Введите Telegram ID');
      return;
    }
    
    setLinkingTelegram(true);
    
    try {
      const result = await linkTelegramId(telegramId.trim());
      alert(result.message || 'Telegram ID успешно привязан');
      const updatedUser = { ...user, telegram_id: result.telegram_id };
      localStorage.setItem('voidlink_user', JSON.stringify(updatedUser));
      loadSubscription();
    } catch (error) {
      console.error('Telegram link error:', error);
      alert(error.message || 'Ошибка привязки Telegram ID');
    } finally {
      setLinkingTelegram(false);
    }
  };

  const handleLogout = () => {
    clearAuth();
    navigate('/login');
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Неактивна';
    const date = new Date(dateString);
    return date.toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div className="dashboard">
      <div className="dashboard-card">
        <div className="dashboard-header">
          <img src={logo} alt="VoidLink" className="dashboard-logo-img" />
          <h1 className="dashboard-logo">VoidLink</h1>
          <p className="dashboard-subtitle">Добро пожаловать, {user?.username}!</p>
        </div>

        <div className="dashboard-tabs">
          <button 
            className={`dashboard-tab ${activeTab === 'subscription' ? 'active' : ''}`}
            onClick={() => setActiveTab('subscription')}
          >
            Моя подписка
          </button>
          <button 
            className={`dashboard-tab ${activeTab === 'buy' ? 'active' : ''}`}
            onClick={() => setActiveTab('buy')}
          >
            Купить
          </button>
        </div>

        {activeTab === 'subscription' && (
          <div className="dashboard-content">
            {subscription?.telegram_id && (
              <div className="telegram-linked">
                <span className="telegram-label">Ваш Telegram ID:</span>
                <span className="telegram-value">{subscription.telegram_id}</span>
              </div>
            )}
            
            {!subscription?.telegram_id && (
              <div className="telegram-link-section">
                <p className="telegram-instruction">
                  Введите свой Telegram ID для связи подписки с аккаунтом в боте. 
                  Узнать ID можно командой /myid в боте 
                  <a href="https://t.me/voidlinkvpntun_bot" target="_blank" rel="noopener noreferrer" className="telegram-bot-link">
                    @voidlinkvpntun_bot
                  </a>
                </p>
                <div className="telegram-input-group">
                  <input
                    type="text"
                    placeholder="Telegram ID"
                    value={telegramId}
                    onChange={(e) => setTelegramId(e.target.value)}
                    className="telegram-input"
                  />
                  <button
                    className="telegram-button"
                    onClick={handleLinkTelegram}
                    disabled={linkingTelegram}
                  >
                    {linkingTelegram ? 'Проверка...' : 'Привязать'}
                  </button>
                </div>
              </div>
            )}
            
            {subLoading ? (
              <p className="dashboard-loading">Загрузка подписки...</p>
            ) : subscription && subscription.active ? (
              <div className="subscription-active">
                <div className="subscription-status">
                  <span className="status-label">Статус:</span>
                  <span className="status-value active">Активна</span>
                </div>
                <div className="subscription-expire">
                  <span className="expire-label">Действует до:</span>
                  <span className="expire-value">
                    {subscription.expireDate ? formatDate(subscription.expireDate) : 'Неизвестно'}
                  </span>
                </div>
                {subscription.connectionLink && (
                  <div className="subscription-link">
                    <span className="link-label">Ссылка для подключения:</span>
                    <div className="link-input-group">
                      <input
                        type="text"
                        value={subscription.connectionLink}
                        readOnly
                        className="link-input"
                      />
                      <button 
                        className="copy-button"
                        onClick={() => handleCopy(subscription.connectionLink)}
                      >
                        Копировать
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="subscription-inactive">
                <p className="inactive-text">У вас нет активной подписки</p>
                <button 
                  className="tariff-button"
                  onClick={() => setActiveTab('buy')}
                >
                  Выбрать тариф
                </button>
              </div>
            )}
            
            <div className="support-contact">
              <span className="support-label">Поддержка:</span>
              <a href="https://t.me/suppVoidlinkVPN" target="_blank" rel="noopener noreferrer" className="support-link">
                @suppVoidlinkVPN
              </a>
            </div>
          </div>
        )}

        {activeTab === 'buy' && (
          <div className="dashboard-content">
            {loading ? (
              <p className="dashboard-loading">Загрузка тарифов...</p>
            ) : (
              <>
                <div className="promo-section">
                  <input
                    type="text"
                    placeholder="Промокод (опционально)"
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value)}
                    className="promo-input"
                  />
                </div>
                
                <div className="tariffs-grid">
                  {tariffs && Object.entries(tariffs).map(([key, tariff]) => (
                    <div key={key} className="tariff-card">
                      <h3 className="tariff-title">{tariff.title}</h3>
                      <div className="tariff-price">{tariff.price}₽</div>
                      <p className="tariff-description">{tariff.description}</p>
                      <button
                        className="tariff-button"
                        onClick={() => handlePayment(key)}
                        disabled={processing && selectedTariff === key}
                      >
                        {processing && selectedTariff === key ? 'Обработка...' : 'Купить'}
                      </button>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        <button className="dashboard-logout" onClick={handleLogout}>
          Выйти
        </button>
      </div>
    </div>
  );
}

export default Dashboard;
